import { useEffect, useState } from "react";
import { LANGS } from "@/data/translations";
import { useLang } from "@/context/LanguageContext";
import { NAV } from "@/constants/testIds";
import { Menu, X } from "lucide-react";

const LOGO = `${process.env.PUBLIC_URL || ""}/brand/MB_horizontal.webp`;

export const Navbar = () => {
  const { lang, setLang, tr } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const links = [
    { href: "#bio", label: tr.nav.bio, testId: NAV.linkBio },
    { href: "#profile", label: tr.nav.profile, testId: NAV.linkProfile },
    { href: "#trajectory", label: tr.nav.trajectory, testId: NAV.linkTrajectory },
    { href: "#achievements", label: tr.nav.achievements, testId: NAV.linkAchievements },
    { href: "#video", label: tr.video?.section || "Video", testId: "nav-link-video" },
    { href: "#gallery", label: tr.nav.gallery, testId: NAV.linkGallery },
    { href: "#contact", label: tr.nav.contact, testId: NAV.linkContact },
  ];

  return (
    <nav
      data-testid={NAV.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? "bg-[color:var(--sky)] border-b border-[color:var(--paris-navy)]/10"
          : "bg-[color:var(--sky)]"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 flex items-center justify-between h-20 md:h-24 lg:h-28 gap-4">
        <a href="#top" data-testid={NAV.logo} aria-label="Manoly Baquerizo" className="flex items-center h-full py-2 shrink-0 min-w-0">
          <img
            src={LOGO}
            alt="Manoly Baquerizo"
            width="520"
            height="100"
            style={{ width: "220px", height: "auto", display: "block" }}
            className="lg:!w-[260px]"
            decoding="async"
          />
        </a>

        {/* Desktop links — inline from 1200px (xl) */}
        <div className="hidden xl:flex items-center gap-7 2xl:gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testId}
              className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--paris-navy)] hover:opacity-70 transition-opacity duration-200 font-semibold"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 border border-[color:var(--paris-navy)]/35 rounded-full p-0.5">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={NAV.langSwitch(l.code)}
                onClick={() => setLang(l.code)}
                aria-label={`Language ${l.label}`}
                className={`text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-full transition-colors duration-200 font-semibold ${
                  lang === l.code
                    ? "bg-[color:var(--paris-navy)] text-[color:var(--sky)]"
                    : "text-[color:var(--paris-navy)]/80 hover:text-[color:var(--paris-navy)]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <button
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            className="xl:hidden w-11 h-11 flex items-center justify-center border border-[color:var(--paris-navy)]/35 rounded-full text-[color:var(--paris-navy)]"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu — single CTA + links */}
      {open && (
        <div className="xl:hidden bg-[color:var(--sky)] border-t border-[color:var(--paris-navy)]/15 no-blur-mobile">
          <div className="px-5 py-7 flex flex-col gap-5 max-h-[80vh] overflow-y-auto">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`${l.testId}-mobile`}
                onClick={() => setOpen(false)}
                className="text-sm tracking-[0.22em] uppercase text-[color:var(--paris-navy)] font-semibold"
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-2 pt-2">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`${NAV.langSwitch(l.code)}-mobile`}
                  onClick={() => setLang(l.code)}
                  className={`text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-full border font-semibold ${
                    lang === l.code
                      ? "bg-[color:var(--paris-navy)] text-[color:var(--sky)] border-[color:var(--paris-navy)]"
                      : "border-[color:var(--paris-navy)]/35 text-[color:var(--paris-navy)]/80"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="btn-pill mt-3 justify-center"
              data-testid="nav-mobile-cta"
            >
              {tr.nav.contact}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};
